import {
  createDraftSafeSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";

import { priorCarriers } from "@covertree/consts";
import {
  ContinueQuotePolicyFragmentFragment,
  PolicyUsage,
  PriorInsurance,
  PriorInsuranceDetailsInput,
  PurchaseDate,
  UsageInput,
} from "@covertree/new-business-graphql";
import {
  EffectiveDateExposureAndLapseForm,
  PolicyUsageD2CForm,
} from "@covertree/new-business-forms";
import { HomeUsage, OwnershipType, ResidencyType } from "@covertree/types";

import {
  getHomeUsageBasedOnPolicyUsage,
  getOwnershipTypeBasedOnPolicyUsage,
  getPolicyUsage,
  getResidencyTypeBasedOnPolicyUsage,
} from "../../utils";

type Usage = UsageInput & {
  homePurchaseTimeline: PurchaseDate;
  residencyType: ResidencyType;
  ownershipType: OwnershipType;
};

interface BasicPolicyDetails {
  policyEffectiveDate?: string;
  priorInsuranceDetails: Partial<PriorInsuranceDetailsInput>;
  usage: Partial<Usage>[];
  unitCount?: string;
}

const initialState: BasicPolicyDetails = {
  unitCount: undefined,
  policyEffectiveDate: undefined,
  priorInsuranceDetails: {
    priorInsurance: undefined,
    priorCarrierName: undefined,
    priorPolicyExpirationDate: undefined,
  },
  usage: [
    {
      homePurchaseTimeline: undefined,
      policyUsage: undefined,
      vacancyReason: undefined,
      residencyType: undefined,
      ownershipType: undefined,
    },
  ],
};

export const basicPolicyDetailsSlice = createSlice({
  name: "basicPolicyDetails",
  initialState,
  selectors: {
    selectFormDefaultValues: (
      state
    ): Partial<EffectiveDateExposureAndLapseForm> => {
      const homeUsage = getHomeUsageBasedOnPolicyUsage(
        state.usage[0].policyUsage
      );
      const ownershipType =
        state.usage[0].ownershipType ??
        getOwnershipTypeBasedOnPolicyUsage(state.usage[0].policyUsage);
      const residencyType = getResidencyTypeBasedOnPolicyUsage(
        state.usage[0].policyUsage
      );

      const priorInsuranceCarrierName =
        state.priorInsuranceDetails.priorCarrierName;

      return {
        homePurchaseTimeline: state.usage[0].homePurchaseTimeline,
        homeUsage,
        ownershipType,
        policyEffectiveDate: state.policyEffectiveDate,
        priorInsuranceCarrierName: priorInsuranceCarrierName
          ? {
              label: priorCarriers.find(
                ({ value }) => value === priorInsuranceCarrierName
              )!.label,
              value: priorInsuranceCarrierName,
            }
          : undefined,
        priorInsuranceExpirationTimeline:
          state.priorInsuranceDetails.priorPolicyExpirationDate ?? undefined,
        priorInsuranceSituationStatus:
          state.priorInsuranceDetails.priorInsurance ?? undefined,
        residencyType,
        vacancyReason: state.usage[0].vacancyReason ?? undefined,
      };
    },
    selectD2CFormDefaultValues: (
      state
    ): Partial<EffectiveDateExposureAndLapseForm> => {
      const homeUsage = getHomeUsageBasedOnPolicyUsage(
        state.usage[0].policyUsage
      );
      const ownershipType = getOwnershipTypeBasedOnPolicyUsage(
        state.usage[0].policyUsage
      );
      const residencyType = getResidencyTypeBasedOnPolicyUsage(
        state.usage[0].policyUsage
      );

      return {
        homeUsage,
        ownershipType,
        residencyType,
      };
    },
    selectOwnershipType: (state) => state.usage[0].ownershipType,
    selectResidencyType: (state) => state.usage[0].residencyType,
    selectTimeSpentAtHome: (state) => {
      switch (state.usage[0].policyUsage) {
        case PolicyUsage.Owner:
          return HomeUsage.MainResidence;
        case PolicyUsage.Seasonal:
          return HomeUsage.SeasonalResidence;
        case PolicyUsage.Vacant:
          return HomeUsage.HomeIsVacant;
        default:
          return undefined;
      }
    },
    selectEffectiveDate: (state) => state.policyEffectiveDate,
    selectPriorInsuranceDetails: (state) => state.priorInsuranceDetails,
    selectPolicyUsage: (state) => state.usage[0].policyUsage,
    selectUsage: (state) => state.usage[0],
    selectVacancyReason: (state) => state.usage[0].vacancyReason,
    selectIsTenantFlow: (state) =>
      state.usage[0].policyUsage === PolicyUsage.Tenant,
    selectUnitCount: (state) => state.unitCount,
  },
  reducers: {
    setBasicPolicyDetails(
      state,
      { payload }: PayloadAction<EffectiveDateExposureAndLapseForm>
    ) {
      state.policyEffectiveDate = payload.policyEffectiveDate;
      state.priorInsuranceDetails = {
        priorInsurance: payload.priorInsuranceSituationStatus,
        priorCarrierName: payload.priorInsuranceCarrierName?.value,
        priorPolicyExpirationDate: payload.priorInsuranceExpirationTimeline,
      };

      const policyUsage = getPolicyUsage(
        state.usage[0].ownershipType ?? payload.ownershipType,
        payload.residencyType,
        payload.homeUsage!
      );

      state.usage[0] = {
        ownershipType: state.usage[0].ownershipType,
        homePurchaseTimeline: payload.homePurchaseTimeline,
        policyUsage,
        vacancyReason: payload.vacancyReason,
      };
    },
    /**
     *
     * Set basic policy details based on policy object in CYQ
     */
    setBasicPolicyDetailsBasedOnPolicy(
      state,
      {
        payload: { startDate, priorInsuranceDetails, units },
      }: PayloadAction<
        Pick<
          ContinueQuotePolicyFragmentFragment,
          "startDate" | "priorInsuranceDetails" | "units"
        >
      >
    ) {
      state.policyEffectiveDate = startDate;
      state.priorInsuranceDetails = {
        priorInsurance: priorInsuranceDetails.priorInsurance,
        ...(priorInsuranceDetails.priorCarrierName
          ? { priorCarrierName: priorInsuranceDetails.priorCarrierName }
          : {}),
        ...(priorInsuranceDetails.priorPolicyExpirationDate
          ? {
              priorPolicyExpirationDate:
                priorInsuranceDetails.priorPolicyExpirationDate,
            }
          : {}),
      };

      const ownershipType =
        units[0].usage === PolicyUsage.Rental
          ? OwnershipType.Landlord
          : OwnershipType.Resident;

      if (ownershipType === OwnershipType.Landlord)
        state.unitCount = "single-unit";

      state.usage[0] = {
        ...state.usage[0],
        policyUsage: units[0].usage,
        ownershipType,
        ...(units[0].details.purchaseDate
          ? { homePurchaseTimeline: units[0].details.purchaseDate }
          : {}),
        ...(units[0].vacancyReason
          ? { vacancyReason: units[0].vacancyReason }
          : {}),
      };
    },
    setPolicyUsage(state, { payload }: PayloadAction<PolicyUsageD2CForm>) {
      const policyUsage = getPolicyUsage(
        payload.ownershipType,
        payload.residencyType,
        payload.homeUsage!
      );

      state.usage[0] = {
        ...state.usage[0],
        policyUsage,
      };
    },
    setOwnershipType(state, { payload }: PayloadAction<OwnershipType>) {
      if (!state.usage[0]) {
        state.usage[0] = {
          ownershipType: payload,
          ...(payload === OwnershipType.Landlord
            ? { policyUsage: PolicyUsage.Rental }
            : {}),
        };
        return;
      }

      const ownershipTypeChanged = state.usage[0].ownershipType !== payload;

      if (!ownershipTypeChanged) {
        return;
      }

      state.priorInsuranceDetails = {};
      if (payload === OwnershipType.Resident) state.unitCount = undefined;

      state.usage[0] = {
        ownershipType: payload,
        policyUsage:
          payload === OwnershipType.Landlord ? PolicyUsage.Rental : undefined,
      };
    },
    setPolicyEffectiveDate(state, { payload }: PayloadAction<string>) {
      state.policyEffectiveDate = payload;
    },
    setPriorInsuranceDetails(
      state,
      { payload }: PayloadAction<Partial<PriorInsuranceDetailsInput>>
    ) {
      let {
        priorInsuranceDetails: {
          // eslint-disable-next-line prefer-const
          priorInsurance,
          priorPolicyExpirationDate,
          priorCarrierName,
        },
      } = state;

      if (payload?.priorInsurance) {
        if (
          state.usage[0].policyUsage === PolicyUsage.Tenant ||
          payload.priorInsurance !== PriorInsurance.NoInsurance
        ) {
          priorPolicyExpirationDate = undefined;
        }

        if (payload.priorInsurance !== PriorInsurance.SwitchingCarriers) {
          priorCarrierName = undefined;
        }

        if (payload.priorInsurance !== PriorInsurance.BuyingNewHome) {
          state.usage[0] = {
            ...state.usage[0],
            homePurchaseTimeline: undefined,
          };
        }
      }

      state.priorInsuranceDetails = {
        priorInsurance,
        priorCarrierName,
        priorPolicyExpirationDate,
        ...payload,
      };
    },
    setUsage(state, { payload }: PayloadAction<Partial<Usage>>) {
      state.usage[0] = {
        ...state.usage[0],
        ...payload,
      };
    },
    setUnitCount(state, { payload }: PayloadAction<string>) {
      state.unitCount = payload;
    },
    resetBasicPolicyDetails(state) {
      return {
        ...initialState,
        unitCount: state.unitCount,
        usage: [
          {
            ...initialState.usage[0],
            ownershipType: state.usage[0].ownershipType,
            policyUsage: state.usage[0].policyUsage,
          },
        ],
      };
    },
    resetProductSelection(state) {
      return {
        ...state,
        unitCount: undefined,
        usage: [
          {
            ...state.usage[0],
            ownershipType: undefined,
            policyUsage: undefined,
          },
        ],
      };
    },
  },
});

export const {
  actions: {
    setBasicPolicyDetails,
    setBasicPolicyDetailsBasedOnPolicy,
    setPolicyEffectiveDate,
    setPriorInsuranceDetails,
    setUsage,
    setPolicyUsage,
    setOwnershipType,
    setUnitCount,
    resetBasicPolicyDetails,
    resetProductSelection,
  },
  selectors: {
    selectEffectiveDate,
    selectFormDefaultValues,
    selectD2CFormDefaultValues,
    selectPriorInsuranceDetails,
    selectPolicyUsage,
    selectUsage,
    selectVacancyReason,
    selectOwnershipType,
    selectResidencyType,
    selectTimeSpentAtHome,
    selectIsTenantFlow,
    selectUnitCount,
  },
} = basicPolicyDetailsSlice;

/**
 * Draft Selector, which grabs state of whole slice needed to API call
 * Can be used within reducers of other slices
 */
export const getBasicPolicyDetails = createDraftSafeSelector(
  [selectEffectiveDate, selectPriorInsuranceDetails, selectUsage],
  (effectiveDate, priorInsuranceDetails, usage) => ({
    effectiveDate,
    priorInsuranceDetails,
    usage,
  })
);
